/**
 * Whether reloading the page right now could lose something the user wrote.
 *
 * App-wide and framework-free (bar the one reporting hook), so code that is
 * not a component — the update checker deciding whether it may reload a
 * hidden tab — can ask. Three inputs:
 *
 *   - **Edits not yet sent**: an editor holding typing in memory (a debounce
 *     timer, an open email draft) reports it here by id. Nothing else can see
 *     a debounce timer, and a reload drops it silently.
 *   - **The save-status store**: a write in flight (`saving`), or a failed
 *     write still carrying its retry — the retry closure holds the content,
 *     and it lives only in this page.
 *   - **The offline outbox** (only for `isWorkSettled`): queued writes are
 *     durable in IndexedDB and survive a reload, but an unattended reload
 *     waits for the queue to drain rather than interrupting a sync.
 */
import { useEffect, useId } from 'react';

import { getOutboxState, subscribeToOutboxState } from '@shared/offline';
import {
  getSnapshot as getSaveStatus,
  subscribe as subscribeToSaveStatus,
} from '@shared/status/saveStatusStore';

const holders = new Set<string>();
const listeners = new Set<() => void>();

/** Tell every subscriber something may have changed. */
function emit(): void {
  for (const listener of listeners) listener();
}

/** Record whether `id` is holding unsent edits; notifies only on a change. */
export function reportUnsavedEdits(id: string, unsaved: boolean): void {
  if (unsaved === holders.has(id)) return;
  if (unsaved) holders.add(id);
  else holders.delete(id);
  emit();
}

/** True when a reload now would lose edits: unsent, mid-save, or failed with a retry. */
export function hasUnsavedWork(): boolean {
  if (holders.size > 0) return true;
  return getSaveStatus().sources.some(
    s => s.state === 'saving' || (s.state === 'failed' && s.retry !== undefined)
  );
}

/** True when nothing is unsaved AND the outbox has nothing queued or syncing. */
export function isWorkSettled(): boolean {
  if (hasUnsavedWork()) return false;
  const outbox = getOutboxState();
  return outbox.pendingCount === 0 && !outbox.syncing;
}

/** Be told when any input changes. Returns the unsubscribe. */
export function subscribeToUnsavedWork(listener: () => void): () => void {
  listeners.add(listener);
  const offSaveStatus = subscribeToSaveStatus(listener);
  const offOutbox = subscribeToOutboxState(listener);
  return () => {
    listeners.delete(listener);
    offSaveStatus();
    offOutbox();
  };
}

/**
 * Report `unsaved` for the calling component while it is mounted. Keyed to the
 * value, so re-running on an `<Activity>` show re-reports the current truth.
 */
export function useReportUnsavedEdits(unsaved: boolean): void {
  const id = useId();
  useEffect(() => {
    reportUnsavedEdits(id, unsaved);
    return () => reportUnsavedEdits(id, false);
  }, [id, unsaved]);
}

/** Tests only: forget every holder and listener. */
export function resetUnsavedWork(): void {
  holders.clear();
  listeners.clear();
}
